import { EnvironmentProviders, inject, makeEnvironmentProviders, Provider } from '@angular/core';
import { provideDataConnect } from '@angular/fire/data-connect';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';
import { connectStorageEmulator, getStorage, provideStorage } from '@angular/fire/storage';
import { NovaPortalLibConfigService } from '@nova-portal/frontend-lib';
import { connectDataConnectEmulator, ConnectorConfig, getDataConnect } from 'firebase/data-connect';
import { PICTURES_STORAGE } from './api-client.tokens';
import { ApiFirebaseClient } from './api-firebase-client.service';
import { ApiFireStorageClient } from './api-firestorage-client.service';
import { ApiFireStoragePicturesClient } from './api-firestorage-pictures-client.service';
import { ApiFirestoreClient } from './api-firestore-client.service';

export const apiClientProviders: (Provider | EnvironmentProviders)[] = [
	provideDataConnect(() => {
		const config = inject(NovaPortalLibConfigService).getConfig();
		const dbConnect = getDataConnect(config.firebaseDataConnectorConfig ?? ({} as ConnectorConfig));
		if (config.useDBEmulator) {
			console.log('Use DB Emulator');
			connectDataConnectEmulator(dbConnect, 'localhost', 9399);
		}
		return dbConnect;
	}),
	provideFirestore(() => {
		const config = inject(NovaPortalLibConfigService).getConfig();
		const firestore = getFirestore();
		if (config.useDBEmulator) {
			console.log('Use DB Emulator');
			connectFirestoreEmulator(firestore, 'localhost', 8080);
		}
		return firestore;
	}),
	provideStorage(() => {
		const config = inject(NovaPortalLibConfigService).getConfig();
		const storage = getStorage();
		if (config.useStorageEmulator) {
			console.log('Use Storage Emulator');
			connectStorageEmulator(storage, 'localhost', 9199);
		}
		return storage;
	}),
	{
		provide: PICTURES_STORAGE,
		useFactory: () => {
			const config = inject(NovaPortalLibConfigService).getConfig();
			const bucket = config.picturesStorageBucket;
			if (!bucket) {
				return null;
			}

			const storage = getStorage(undefined, bucket.startsWith('gs://') ? bucket : `gs://${bucket}`);
			if (config.useStorageEmulator) {
				console.log('Use Storage Emulator for pictures');
				connectStorageEmulator(storage, 'localhost', 9199);
			}
			return storage;
		}
	},
	provideFunctions(() => {
		const config = inject(NovaPortalLibConfigService).getConfig();
		const functions = getFunctions(undefined, 'europe-west3');
		if (config.useFunctionsEmulator) {
			console.log('Use Functions Emulator');
			connectFunctionsEmulator(functions, 'localhost', 5001);
		}
		return functions;
	}),
	ApiFirebaseClient,
	ApiFirestoreClient,
	ApiFireStorageClient,
	ApiFireStoragePicturesClient
];

/**
 * Provides Firebase API clients and their configured AngularFire services.
 * @returns Environment providers for Firebase API access.
 */
export const provideApiClient = (): EnvironmentProviders => makeEnvironmentProviders(apiClientProviders);
